import React, { useCallback, useEffect, useState } from 'react';
import userflow from 'userflow.js';
import LegalKitzLogo from '../../../assets/svg/legalkitz-logo.svg?react';
import PromoContracts from '../../../assets/svg/promo-contracts.svg?react';
import PromoEmployment from '../../../assets/svg/promo-employment.svg?react';
import PromoIpProtection from '../../../assets/svg/promo-ip-protection.svg?react';
import PromoStructuring from '../../../assets/svg/promo-structuring.svg?react';
import ListItemBlock from '../../common/list-item-block/ListItemBlock';
import { ROUTES } from '../../common/constants/routes';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Modal,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '../../../assets/svg/add-icon.svg?react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TaskItem from '../../common/task-item-block/task-item-block';
import ListItemButton from '../../common/list-item-button/ListItemButton';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { deleteTodo, fetchTodos, updateTodo } from '../../slices/todo/todoSlice';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import TodoForm from './components/TodoForm/TodoForm';
import { ToDoStatus } from '../../common/task-item-block/types';
import { ToDo } from '../../models/todo.model';
import CompanyCreateForm from '../../common/company-create-form';
import { fetchCompanies, selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchEmployees, selectAllEmployees } from '../../slices/employees/employeesSlice';
import { fetchSuppliers } from '../../slices/suppliers/suppliersSlice';
import { fetchCustomers } from '../../slices/customers/customersSlice';
import { fetchDocuments, selectAllDocuments } from '../../slices/documents/documentsSlice';
import {
  addDocumentContract,
  selectAllDocumentContracts,
} from '../../slices/document-contracts/documentContractsSlice';
import { selectUser } from '../../slices/user/userSlice';
import { RootState } from '../../store';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import { AIDocumentContractModel } from '../../models/ai.document.contract.model';
import { NWClient } from '../../client/NWClient';
import { LibraryItem } from '../library/components/library-list/types';
import { showMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { Helpers } from '../../common/helpers/helpers';
import { toast } from 'react-toastify';
import BookACallModal from '../../common/book-a-call-modal/BookACallModal';
import { ROLES } from '../../common/constants/groups';
import { RolesGate } from '../../common/roles-gate';
import EmployeeDetailsCard from '../../common/employee-details-card/EmployeeDetailsCard';
import { DocumentVaultItemModel } from '../../models/document.vault.item.model';
import { fetchTemplates, selectAllBKTemplates } from '../../slices/templates/templatesSlice';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { useHasClerkRole } from '../../common/hooks/useHasClerkRole';
import { USER_PERMISSIONS } from '../../common/constants/permissions';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';
import PostAddIcon from '@mui/icons-material/PostAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditDocumentIcon from '@mui/icons-material/EditDocument';

const HomePageBlock: React.FC = () => {
  const currentUser = useAppSelector(selectUser);
  const userIsEmployee = useHasClerkRole('org:employee');
  const templates = useAppSelector(selectAllBKTemplates);
  const templatesLoading = useAppSelector((state: RootState) => state.templates.status);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const todosPending = useAppSelector((state: RootState) => state.todos.status);
  const fetchingCompanyError = useAppSelector((state: RootState) => state.companies.error);
  const checkPermission = useCheckPermission();

  const templatesFilteredSorted = templates
    ?.filter((item: LibraryItem) => item.top_template && item.top_template_order)
    .sort((a, b) => Number(a.top_template_order) - Number(b.top_template_order));

  const dispatch = useAppDispatch();
  const todos = useAppSelector((state) => state.todos.items);
  const company = useAppSelector(selectAllCompanies)[0];
  const freePlan = !company?.stripe_product_id && !company?.plan_settings.stripe_product_id;

  const employees = useAppSelector(selectAllEmployees);
  const documentUploads = useAppSelector(selectAllDocuments);
  const documentContracts = useAppSelector(selectAllDocumentContracts);
  const employee =
    userIsEmployee &&
    employees.find(
      (employee) =>
        employee.email_address.toLowerCase() === currentUser.email.toLowerCase() ||
        employee.business_email_address.toLowerCase() === currentUser.email.toLowerCase()
    );
  const [isSkipped, setIsSkipped] = useState(true);
  const [pendingDownload, setPendingDownload] = useState<boolean>(false);
  const [todosLoading, setTodosLoading] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [createNewDocument, setCreateNewDocument] = useState<string>('selectAnOption');
  const [isBlankModalOpen, setIsBlankModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [documentsWithKeyDates, setDocumentsWithKeyDates] = useState<
    DocumentVaultItemModel[] | AIDocumentContractModel[]
  >([]);
  const [, setDocumentsWithKeyDatesLoading] = useState<boolean>(false);
  const [documentsWithKeyDatesOverdue, setDocumentsWithKeyDatesOverdue] = useState<
    DocumentVaultItemModel[] | AIDocumentContractModel[]
  >([]);
  const [showDocsWithKeyDatesOverdue, setShowDocsWithKeyDatesOverdue] = useState<boolean>(false);

  function formatDate(date: Date): string {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  const documentsWithKeyDatesData = [
    ...(showDocsWithKeyDatesOverdue ? documentsWithKeyDatesOverdue : documentsWithKeyDates),
  ]
    .slice()
    .sort(function (a, b) {
      return (
        Number(new Date((showDocsWithKeyDatesOverdue ? b : a).key_date_end_date)) -
        Number(new Date((showDocsWithKeyDatesOverdue ? a : b).key_date_end_date))
      );
    })
    .map((el) => ({
      id: el.id,
      labelText: formatDate(new Date(el.key_date_end_date)),
      withDate: true,
      name: el.name,
      onListItemClick:
        'contract_generation' in el
          ? () => navigate(`${ROUTES.DOCUMENT_VAULT}/${el.id}?initialTab=0&contractGenerated=true`)
          : () => navigate(`${ROUTES.DOCUMENT_VAULT}/${el.id}`),
    }));

  const recentDocumentsArraySorted = [...documentUploads, ...documentContracts]
    .slice()
    .sort(function (a, b) {
      return Number(new Date(b.updated)) - Number(new Date(a.updated));
    })
    .slice(0, 4);

  const toggleShowBookingModal = () => {
    setIsBookingModalOpen((prev) => !prev);
  };

  const navigateToTemplatePage = (category: string, id: number) => {
    navigate(`${ROUTES.DOCUMENT_LIBRARY}/${category}/${id}?templateViewEditMode=true`);
  };

  const handleS3DownloadLink = (id: number, filename: string) => {
    setPendingDownload(true);
    NWClient.downloadLibraryDocument(id, filename)
      .catch((error) => {
        const errorMessage = Helpers.returnErrorText(error);
        toast.error(errorMessage.toString());
      })
      .finally(() => {
        setPendingDownload(false);
      });
  };

  const handleNavigateTemplatePage = (item: LibraryItem) => {
    const actionAvailableFree = 'is_free' in item && item.is_free;
    if (item.download_link) {
      handleS3DownloadLink(item.id, item.name);
    } else {
      if (freePlan) {
        if (actionAvailableFree) {
          navigateToTemplatePage('region' in item ? item.region : 'company-templates', item.id);
        } else {
          dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
        }
      } else {
        navigateToTemplatePage('region' in item ? item.region : 'company-templates', item.id);
      }
    }
  };

  const templatesArray = [...templatesFilteredSorted].map((el) => {
    return {
      id: el.id,
      name: el.name,
      isFreeLabel: el.is_free,
      onListItemClick: () => handleNavigateTemplatePage(el),
    };
  });

  const recentDocuments = recentDocumentsArraySorted.map((el) => ({
    id: el.id,
    name: el.name,
    labelText: el.category,
    onListItemClick:
      'contract_generation' in el
        ? () => navigate(`${ROUTES.DOCUMENT_VAULT}/${el.id}?initialTab=0&contractGenerated=true`)
        : () => navigate(`${ROUTES.DOCUMENT_VAULT}/${el.id}`),
  }));

  useEffect(() => {
    if (isSkipped && company && (todosPending === 'idle' || todosPending === 'failed')) {
      dispatch(fetchTodos());
    }
  }, [isSkipped, company, todosPending, dispatch]);

  const handleSkip = () => {
    setIsSkipped(true);
  };
  const handleAction = (action: string, todo: any) => {
    if (action === 'delete') {
      setTodosLoading(true);
      dispatch(deleteTodo({ id: todo.id }))
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
    if (action === 'modify') {
      setSelectedTodo(todo);
      setOpen(true);
    }
  };
  const handleStart = (todoId: number) => {
    const todo = todos.find((t) => t.id === todoId);
    if (todo) {
      setTodosLoading(true);
      dispatch(
        updateTodo({
          id: todoId,
          data: {
            ...todo,
            started_at: new Date().toISOString(),
          },
        })
      )
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
  };

  const handleComplete = (todoId: number) => {
    const todo = todos.find((t) => t.id === todoId);
    if (todo) {
      setTodosLoading(true);
      dispatch(
        updateTodo({
          id: todoId,
          data: {
            ...todo,
            completed_at: new Date().toISOString(),
          },
        })
      )
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
  };

  const handleContinue = (todoId: number) => {
    const todo = todos.find((t) => t.id === todoId);
    if (todo) {
      setTodosLoading(true);
      dispatch(
        updateTodo({
          id: todoId,
          data: {
            ...todo,
            completed_at: null,
            started_at: new Date().toISOString(),
          },
        })
      )
        .unwrap()
        .finally(() => setTodosLoading(false));
    }
  };

  const getTodoStatus = (todo: ToDo): ToDoStatus => {
    if (todo.completed_at) {
      return 'completed';
    }
    const now = new Date();
    const dueDate = new Date(todo.due_at);
    if (now > dueDate) {
      return 'overdue';
    }
    return 'in_progress';
  };
  const sortTodos = (todos: ToDo[]): ToDo[] => {
    const groups: { [key in ToDoStatus]: ToDo[] } = {
      overdue: [],
      in_progress: [],
      completed: [],
    };

    todos.forEach((todo) => {
      const status = getTodoStatus(todo);
      groups[status].push(todo);
    });
    groups.overdue.sort((a, b) => new Date(b.due_at).getTime() - new Date(a.due_at).getTime());
    groups.in_progress.sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime());
    return [...groups.overdue, ...groups.in_progress, ...groups.completed];
  };

  const handleOpen = () => {
    setSelectedTodo(null);
    setOpen(true);
  };
  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      return;
    }
    setOpen(false);
    setSelectedTodo(null);
  };
  useEffect(() => {
    switch (createNewDocument) {
      case 'createFromBusinessKitzLibrary':
        navigate(ROUTES.DOCUMENT_LIBRARY + '?businessKitzLibrary=true');
        break;
      case 'createFromOwnLibrary':
        navigate(ROUTES.DOCUMENT_LIBRARY + '?ownLibrary=true');
        break;
      case 'createNewBlankDocument':
        setIsBlankModalOpen(true);
        break;
    }
  }, [createNewDocument, navigate]);
  useEffect(() => {
    const closeHandler = () => setOpen(false);
    document.addEventListener('closeModal', closeHandler);
    return () => {
      document.removeEventListener('closeModal', closeHandler);
    };
  }, []);

  const getCompanyAndProfiles = useCallback(() => {
    if (!company) {
      dispatch(fetchCompanies());
    }
    if (company) {
      dispatch(fetchEmployees({ company_id: company.id }));
      dispatch(fetchSuppliers({ company_id: company.id }));
      dispatch(fetchCustomers({ company_id: company.id }));
    }
  }, [company, dispatch]);

  const getDocuments = useCallback(() => {
    if (currentUser?.id && company) {
      dispatch(fetchDocuments({ data: { user: currentUser.id } }));
      /*dispatch(fetchDocumentContracts({ data: { user: currentUser.id } }))
        .unwrap()
        .finally(() => setDocumentContractsLoading(false));*/
    }
  }, [currentUser, dispatch, company]);

  const getTemplates = useCallback(() => {
    if (currentUser?.id && templatesLoading === 'idle') {
      dispatch(fetchTemplates());
    }
  }, [currentUser, templatesLoading, dispatch]);

  const userFlowTrigger = useCallback(() => {
    if (import.meta.env.VITE_USERFLOW_ENABLED === 'TRUE' && currentUser.id) {
      userflow.init(import.meta.env.VITE_USERFLOW_TOKEN);
      const companyPayload = company ? company : null;
      const isCompanyMember = Boolean(
        companyPayload?.members?.some(
          (member) => member?.user_uuid && member.user_uuid === currentUser?.user_uuid
        )
      );
      const isSubscribed = typeof company?.plan_settings?.free === 'object';
      userflow.identify(currentUser.user_uuid, {
        name: currentUser.first_name + ' ' + currentUser.last_name,
        signed_up_at: currentUser.date_joined,
        email: currentUser.email,
        is_company_member: isCompanyMember,
        is_subscribed: isSubscribed,
      });
    }
  }, [
    company,
    currentUser.id,
    currentUser.first_name,
    currentUser.last_name,
    currentUser.email,
    currentUser.date_joined,
    currentUser.user_uuid,
  ]);

  useEffect(() => {
    getCompanyAndProfiles();
  }, [getCompanyAndProfiles]);

  useEffect(() => {
    userFlowTrigger();
  }, [userFlowTrigger]);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);

  useEffect(() => {
    getTemplates();
  }, [getTemplates]);

  useEffect(() => {
    if (currentUser.id) {
      setDocumentsWithKeyDatesLoading(true);
      NWClient.list('key-dates-documents', { user: currentUser?.id })
        .then((res: any) => {
          setDocumentsWithKeyDates(res.results);
        })
        .finally(() => {
          NWClient.list('key-dates-documents/expired', { user: currentUser?.id })
            .then((res: any) => {
              setDocumentsWithKeyDatesOverdue(res.results);
            })
            .finally(() => {
              setDocumentsWithKeyDatesLoading(false);
            });
        });
    }
  }, [currentUser]);

  useEffect(() => {
    if (
      fetchingCompanyError === 'Request failed with status code 401' ||
      fetchingCompanyError === 'Request failed with status code 500'
    ) {
      window.location.reload();
    }
  }, [fetchingCompanyError]);

  const BlankDocumentModal: React.FC<{ isBlankModalOpen: boolean; onClose: () => void }> = ({
    isBlankModalOpen,
    onClose,
  }) => {
    const [documentName, setDocumentName] = useState(
      `${t('buttons.newDocument')} ${Helpers.timeStampToDateV4(new Date().toISOString())}`
    );
    const [documentCategory, setDocumentCategory] = useState('business');
    const handleBlankDocumentCreation = (newname: string, category: string) => {
      const blankDocData = {
        data: {
          user: currentUser.id,
          name: newname,
          category: category,
        },
      };
      dispatch(showOverlay());
      dispatch(addDocumentContract(blankDocData))
        .unwrap()
        .then((res: AIDocumentContractModel) => {
          const data = {
            user: currentUser.id,
            html: `<div>
    <h1  style="margin-bottom: 3pt; text-align: center; break-inside: avoid; break-after: avoid; line-height: 115%; font-size: 26pt;"><span style="font-size: 24px;">New document title (heading 1)</span></h1>
    <p><span>New document paragraph (normal text)</span></p>
</div>`,
          };
          NWClient.patch(`document-contract/${res.id}/`, null, data).finally(() => {
            dispatch(hideOverlay());
            navigate(`${ROUTES.DOCUMENT_VAULT}/${res.id}?initialTab=0&contractGenerated=true`);
          });
        })
        .catch((error) => {
          setIsBlankModalOpen(false);
          setCreateNewDocument('selectAnOption');
          if (error.errors?.length > 0 && error.errors[0] === t('messages.exceededPlanLimits')) {
            dispatch(showMessage(t('messages.monthLimitReached')));
          } else {
            const errorMessage = Helpers.returnErrorText(error);
            toast.error(errorMessage.toString());
          }
        })
        .finally(() => {
          setIsBlankModalOpen(false);
        });
    };
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const newDocumentName = formData.get('newDocumentName') as string;
      const category = formData.get('category') as string;
      handleBlankDocumentCreation(newDocumentName, category);
    };
    return (
      <Modal
        className='action-modal'
        open={isBlankModalOpen}
        sx={{
          position: 'absolute',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          width: '50%',
          top: '0',
          left: '25%',
          margin: 0,
          padding: 0,
        }}
        onClose={onClose}
      >
        <Grid container>
          <Grid size={{ xs: 12, sm: 11, md: 11, lg: 11 }}>
            <Card>
              <CardContent sx={{ paddingBottom: '16px !important' }}>
                <Typography variant={'h6'} gutterBottom>
                  Create new document
                </Typography>

                <Typography>Please provide a name for the document.</Typography>
                <form onSubmit={handleSubmit}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '1rem',
                      marginTop: '0.5rem',
                    }}
                  >
                    <TextField
                      name='newDocumentName'
                      placeholder='New document name'
                      label='Document name'
                      variant='outlined'
                      fullWidth
                      required={true}
                      value={documentName}
                      onChange={(e) => setDocumentName(e.target.value)}
                    />
                    <FormControl fullWidth>
                      <InputLabel required={true} id='demo-simple-select-label'>
                        Category
                      </InputLabel>
                      <Select
                        labelId='demo-simple-select-label'
                        id='demo-simple-select'
                        name='category'
                        label='Category'
                        required={true}
                        value={documentCategory}
                        onChange={(e) => setDocumentCategory(e.target.value)}
                      >
                        <MenuItem value='employment'>Employment</MenuItem>
                        <MenuItem value='customers'>Customers</MenuItem>
                        <MenuItem value='suppliers'>Suppliers</MenuItem>
                        <MenuItem value='business'>Business</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Grid container justifyContent='flex-end' gap={'8px'} mt={2}>
                    <Button
                      variant='contained'
                      onClick={() => setIsBlankModalOpen(false)}
                      className={'btn-default btn-action-basic btn-cancel'}
                    >
                      Go back
                    </Button>

                    <Button
                      variant='contained'
                      type='submit'
                      className='btn-default btn-action-basic'
                      disabled={!documentCategory || !documentName}
                    >
                      Create
                    </Button>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Modal>
    );
  };

  return (
    <>
      {!currentUser.id ? (
        <ProgressOverlay className='position-static' />
      ) : (
        <>
          <div className='d-flex home-page-wrapper'>
            <div className='flex-grow-1 d-flex flex-column'>
              <RolesGate role={ROLES.ADMIN}>
                <div className='d-flex'>
                  <div className='flex-grow-1' style={{ width: '28%' }}>
                    <Box className='box-with-header flex-grow-1'>
                      <Box className='header-action-row d-flex justify-content-between'>
                        <Typography variant='h2'>{t('headers.documents')}</Typography>
                      </Box>
                      <Box
                        gap={'1.1%'}
                        className='d-flex align-items-start flex-wrap buttons-container action-controls-container'
                      >
                        {/*<FormControl>
                      <InputLabel id='createNewDocumentLabel'>
                        {t('labels.createDocument')}
                      </InputLabel>
                      <Select
                        labelId='createNewDocumentLabel'
                        value={createNewDocument}
                        IconComponent={ExpandMoreIcon}
                        MenuProps={{
                          anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                        onChange={(event: SelectChangeEvent) =>
                          setCreateNewDocument(event.target.value)
                        }
                        input={<OutlinedInput />}
                      >
                        <MenuItem value='selectAnOption'>{t('menuItems.selectAnOption')}</MenuItem>
                        <MenuItem value='createFromBusinessKitzLibrary'>
                          {t('menuItems.createFromBusinessKitzLibrary')}
                        </MenuItem>
                        <MenuItem value='createFromOwnLibrary'>
                          {t('menuItems.createFromOwnLibrary')}
                        </MenuItem>
                        {company && (
                          <MenuItem value='createNewBlankDocument'>
                            {t('menuItems.createNewBlankDocument')}
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>*/}
                        {checkPermission(USER_PERMISSIONS.ORG_BLANK_DOCUMENT_CREATE) ? (
                          <ListItemButton
                            buttonIcon={<PostAddIcon />}
                            buttonText={t('buttons.newDocument')}
                            onClick={() => setIsBlankModalOpen(true)}
                          />
                        ) : (
                          <Tooltip title={t('messages.contactAdministrator')} placement='top' arrow>
                            <ListItemButton
                              buttonIcon={<PostAddIcon />}
                              buttonText={t('buttons.newDocument')}
                              onClick={() => setIsBlankModalOpen(true)}
                            />
                          </Tooltip>
                        )}
                        <ListItemButton
                          buttonIcon={<UploadFileIcon />}
                          buttonSecondary={true}
                          buttonText={t('buttons.storeDocuments')}
                          onClick={() => navigate(ROUTES.DOCUMENT_VAULT)}
                        />
                        <ListItemButton
                          buttonIcon={<EditDocumentIcon />}
                          buttonSecondary={true}
                          buttonText={t('buttons.signDocuments')}
                          onClick={() => navigate(ROUTES.DOCUMENTS)}
                        />
                      </Box>
                    </Box>
                  </div>
                  <div className='flex-grow-1' style={{ width: '70%' }}>
                    <Box className='box-with-header box-with-header-tabs flex-grow-1 no-left-right-padding'>
                      <Box
                        className='header-action-row'
                        sx={{
                          marginBottom: '0 !important',
                          paddingLeft: '16px',
                          paddingRight: '16px',
                        }}
                      >
                        <Typography variant='h2'>{t('headers.keyDates')}</Typography>
                      </Box>
                      <Box
                        display={'flex'}
                        alignItems={'flex-start'}
                        sx={{ paddingLeft: '16px', paddingRight: '16px' }}
                      >
                        <Button
                          onClick={() => setShowDocsWithKeyDatesOverdue(false)}
                          className={
                            showDocsWithKeyDatesOverdue ? 'list-item-tab' : 'list-item-tab active'
                          }
                        >
                          {t('labels.upcoming')}
                        </Button>
                        <Button
                          onClick={() => setShowDocsWithKeyDatesOverdue(true)}
                          className={
                            showDocsWithKeyDatesOverdue ? 'list-item-tab active' : 'list-item-tab'
                          }
                        >
                          {t('labels.overdue')}
                        </Button>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: 'auto',
                            fontSize: '12px',
                            height: '30px',
                          }}
                        >
                          {t('labels.endRenewalDate')}
                        </Box>
                      </Box>
                      <ListItemBlock
                        listArray={documentsWithKeyDatesData}
                        withMenu={false}
                        emptyStateMessage={t('emptyStateMessages.keyDatesDocuments')}
                        loading={false}
                      />
                    </Box>
                  </div>
                </div>
              </RolesGate>
              <RolesGate role={ROLES.EMPLOYEE}>
                <div
                  className='d-flex flex-grow-1 flex-column position-relative'
                  style={{ height: '50%', gap: '16px', flexWrap: 'wrap' }}
                >
                  {employee && company ? (
                    <Grid size={{ xs: 12 }}>
                      <EmployeeDetailsCard simplified employee={employee} company={company} />
                    </Grid>
                  ) : (
                    <ProgressOverlay className='position-absolute' />
                  )}
                </div>
              </RolesGate>
              <div
                className='d-flex flex-grow-1 flex-column'
                style={{ height: `calc(50% - 16px)` }}
              >
                <Box className='position-relative box-with-header flex-grow-1 d-flex flex-column'>
                  <Box className='header-action-row d-flex justify-content-between'>
                    <Typography variant='h2'>{t('headers.toDo')}</Typography>
                    {company && (
                      <>
                        {!isSkipped && (
                          <Button
                            variant='contained'
                            className='btn-action-basic btn-small'
                            onClick={handleSkip}
                          >
                            Skip
                          </Button>
                        )}
                        {isSkipped && (
                          <span
                            role='button'
                            tabIndex={-1}
                            onKeyUp={() => {
                              return;
                            }}
                            className='add-icon-wrapper d-flex justify-content-center align-items-center'
                            onClick={handleOpen}
                          >
                            <AddIcon />
                          </span>
                        )}
                      </>
                    )}
                  </Box>
                  {company || userIsEmployee ? (
                    <>
                      {!isSkipped && (
                        <List className='tasks-list pt-0 pb-0'>
                          <ListItem>
                            <TaskItem taskText={'Upload and store a document'} />
                          </ListItem>
                          <ListItem>
                            <TaskItem taskText={'Create new document'} taskCompleted />
                          </ListItem>
                          <ListItem>
                            <TaskItem taskText={'Add employee'} />
                          </ListItem>
                          <ListItem>
                            <TaskItem
                              taskText={'Create a template from existing AI components'}
                              taskCompleted
                            />
                          </ListItem>
                          <ListItem>
                            <TaskItem taskText={'Create new document'} taskCompleted />
                          </ListItem>
                          <ListItem>
                            <TaskItem taskText={'Add a supplier or customer'} />
                          </ListItem>
                          <ListItem>
                            <TaskItem taskText={'Document summary with AI'} taskCompleted />
                          </ListItem>
                          <ListItem>
                            <TaskItem taskText={'Start subscription'} />
                          </ListItem>
                        </List>
                      )}
                      {isSkipped && todos.length === 0 && !todosLoading && (
                        <Box className='d-flex flex-column flex-grow-1 justify-content-center align-items-center empty-state-message-container '>
                          <div className='empty-state-message'>{t('emptyStateMessages.todos')}</div>
                        </Box>
                      )}
                      {isSkipped && todos.length > 0 && (
                        <List className='tasks-list pt-0 pb-0'>
                          {sortTodos(todos).map((todo) => (
                            <ListItem key={todo.id}>
                              <TaskItem
                                taskText={todo.message}
                                date={new Date(todo.due_at)}
                                status={getTodoStatus(todo)}
                                iconLeft
                                onAction={(action) => handleAction(action, todo)}
                                actions
                                onStart={() => handleStart(todo.id)}
                                onComplete={() => handleComplete(todo.id)}
                                onContinue={() => handleContinue(todo.id)}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                      {(todosPending === 'loading' || todosLoading) && (
                        <ProgressOverlay className='position-absolute' />
                      )}
                    </>
                  ) : (
                    <Box className='d-flex flex-column flex-grow-1 justify-content-center align-items-center empty-state-message-container '>
                      <CompanyCreateForm />
                    </Box>
                  )}
                </Box>
              </div>
            </div>
            <div className='flex-shrink-0 d-flex flex-column'>
              <Box className='promo-block' height='100%' minHeight='290px' maxHeight='290px'>
                {/* Logo */}
                <Box className='promo-block__logo'>
                  <LegalKitzLogo />
                </Box>

                {/* Headline */}
                <Typography variant='h2' component='h2' className='promo-block__headline'>
                  {t('legalKitz.promoHeadline')}
                </Typography>

                {/* Subtext */}
                <Typography variant='body2' className='promo-block__subtext'>
                  {t('legalKitz.promoSubtext')
                    .split('\n')
                    .map((line, i, arr) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                </Typography>

                {/* Service icons row */}
                <Box className='promo-block__services'>
                  {[
                    { key: 'promoServiceContracts', Icon: PromoContracts },
                    { key: 'promoServiceEmployment', Icon: PromoEmployment },
                    { key: 'promoServiceIP', Icon: PromoIpProtection },
                    { key: 'promoServiceStructuring', Icon: PromoStructuring },
                  ].map(({ key, Icon }) => (
                    <Box key={key} className='promo-block__service-item'>
                      <Icon />
                      <Typography className='promo-block__service-label'>
                        {t(`legalKitz.${key}`)}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTA Button */}
                <Button
                  variant='contained'
                  onClick={() => toggleShowBookingModal()}
                  className='promo-block__cta-button'
                >
                  {t('legalKitz.promoCtaButton')}
                </Button>
              </Box>
            </div>
          </div>
          <Modal open={open} onClose={handleClose}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: '8px',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '600px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                padding: '16px',
              }}
            >
              {open && <TodoForm existingTodo={selectedTodo} onClose={() => setOpen(false)} />}
            </Box>
          </Modal>
          {isBlankModalOpen && (
            <BlankDocumentModal
              isBlankModalOpen={isBlankModalOpen}
              onClose={() => {
                setIsBlankModalOpen(false);
                setCreateNewDocument('selectAnOption');
              }}
            />
          )}
          {pendingDownload && <ProgressOverlay />}
          {isBookingModalOpen && (
            <BookACallModal
              company={company}
              user={currentUser}
              open={isBookingModalOpen}
              onClose={() => toggleShowBookingModal()}
            />
          )}
        </>
      )}
    </>
  );
};

export default HomePageBlock;
